import React, { useEffect, useMemo, useRef, useState } from "react";
import { Redirect } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import config from "../../../../config/config";
import { apiCall } from "../../../../_services/apiCall";
import { usePermission } from "../../../../hooks/usePermission";
import Sidebar from "../../../sidebar/sidebar";
import Header from "../../../header/header";
import { useQuickView } from "./hooks/useQuickView";
import { getLatestFY } from "../../../DataManagment/Quantative/component/answerHistoryPage/components/utils";
import {
  buildQuickViewMap,
  groupQuestionsByModule,
  getSelectedParentLocations,
} from "./utility/quickViewMapper";
import {
  downloadQuickViewExcel,
  downloadQuickViewExcelPerLocation,
} from "./utility/quickViewExcelBuilder";
import QuickViewFilters from "./components/QuickViewFilters";
import QuickViewHeader from "./components/QuickViewHeader";
import QuickViewTable from "./QuickViewTable";
import "./quickView.css";

/**
 * Renders a centred spinner card while data loads.
 *
 * @param {Object} props
 * @param {string} props.label - Text shown under the spinner.
 * @returns {JSX.Element} The loader card.
 */
const Loader = ({ label }) => (
  <div className="qv-card qv-loader">
    <div className="qv-loader__spinner" />
    <div className="qv-loader__label">{label}</div>
  </div>
);

/**
 * Wraps page content in the app's standard chrome (sidebar + header). The
 * router renders pages bare, so each page draws its own Sidebar/Header.
 *
 * @param {Object} props
 * @param {boolean} props.sidebarExpanded - Whether the sidebar is expanded.
 * @param {Function} props.onSidebarToggle - Receives the sidebar's open state.
 * @param {React.ReactNode} props.children - Page content.
 * @returns {JSX.Element} The page shell.
 */
const QuickViewShell = ({ sidebarExpanded, onSidebarToggle, children }) => (
  <div className="qv-root">
    <div className={`qv-sidebar ${sidebarExpanded ? "qv-sidebar--expanded" : "qv-sidebar--collapsed"}`}>
      <Sidebar onSidebarToggle={onSidebarToggle} />
    </div>

    <main className="qv-main">
      <Header pagetitle="Quick View" />
      <div className="qv-content">{children}</div>
    </main>
  </div>
);

/**
 * Quick View Reporting page. Shows every reporting question's answers laid
 * out period-by-period for a financial year, filterable by location, module
 * and period aggregation, and exportable to Excel.
 *
 * @returns {JSX.Element} The Quick View page.
 */
const QuickView = () => {
  const { hasPermission, capabilitiesLoaded } = usePermission();
  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [financialYear, setFinancialYear] = useState([]);
  const [financialYearId, setFinancialYearId] = useState(null);

  const [locations, setLocations] = useState([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);

  const [subLocations, setSubLocations] = useState([]);
  const [selectedSubLocationIds, setSelectedSubLocationIds] = useState([]);

  const [selectedModules, setSelectedModules] = useState([]);
  const [periodAggregation, setPeriodAggregation] = useState("MONTHLY");
  const [exportMode, setExportMode] = useState("combined");
  const [searchTerm, setSearchTerm] = useState("");

  /**
   * Loads financial years cache-then-fetch: a cached list (if present) paints
   * the page instantly, but the API is always called afterward so a financial
   * year added after the cache was written still shows up this session rather
   * than staying hidden until the user clears storage. The already-selected FY
   * (chosen by the cache, or already changed by the user) is left alone once
   * set; only an initial, still-unset selection is filled in from the fresh
   * fetch. The latest year is picked via getLatestFY (compares financial_year_value,
   * not array position - the API's return order is not a sorting guarantee).
   */
  useEffect(() => {
    const loadFinancialYears = async () => {
      try {
        const storedData = localStorage.getItem("financialYearData");
        let selectedFromCache = false;

        if (storedData) {
          const parsedData = JSON.parse(storedData);
          if (isMounted.current) {
            setFinancialYear(parsedData);
            const latestYear = getLatestFY(parsedData);
            if (latestYear) {
              setFinancialYearId(latestYear.id);
              selectedFromCache = true;
            }
          }
        }

        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getFinancialYear`,
          {},
          {}
        );

        if (isSuccess && isMounted.current) {
          localStorage.setItem("financialYearData", JSON.stringify(data.data));
          setFinancialYear(data.data);
          if (!selectedFromCache) {
            const latestYear = getLatestFY(data.data);
            if (latestYear) setFinancialYearId(latestYear.id);
          }
        }
      } catch (error) {
        console.error("Error loading financial years:", error);
      }
    };

    loadFinancialYears();
  }, []);

  // Locations - fetched once, flattened to parent + sub-location options.
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const { isSuccess, data } = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}getSource`,
          {},
          {},
          "GET"
        );

        if (isSuccess && data?.data && isMounted.current) {
          const parentOptions = [];
          const subOptions = [];
          data.data.forEach((item) => {
            if (!item.id) return;
            const parentLabel =
              item.unitCode || `${item?.location?.area || ""}, ${item?.location?.city || ""}`.trim();
            parentOptions.push({ value: String(item.id), label: parentLabel });
            (item.subLocation || []).forEach((sub) => {
              subOptions.push({
                value: String(sub.id),
                label: sub.subLocation,
                parentValue: String(item.id),
                parentLabel,
              });
            });
          });
          setLocations(parentOptions);
          setSubLocations(subOptions);
        }
      } catch (error) {
        console.error("Error loading locations:", error);
      }
    };

    loadLocations();
  }, []);

  const selectedFinancialYear = useMemo(
    () => financialYear.find((fy) => fy.id === financialYearId),
    [financialYear, financialYearId]
  );
  const financialYearValue = selectedFinancialYear?.financial_year_value;

  const {
    questions,
    filteredQuestions,
    quickViewMap,
    rawRows,
    unitSettingsMap,
    periods,
    loadingQuestions,
    loadingAnswers,
    answersFailed,
    retrying,
    retryAnswers,
  } = useQuickView({
    financialYearId,
    financialYearValue,
    selectedLocationIds,
    selectedSubLocationIds,
    subLocations,
    selectedModuleIds: selectedModules,
    periodAggregation,
    locations,
  });

  const derivedModuleOptions = useMemo(() => {
    const map = new Map();
    questions.forEach((q) => {
      if (q.moduleId != null && !map.has(q.moduleId)) {
        map.set(q.moduleId, { value: q.moduleId, label: q.moduleName });
      }
    });
    return Array.from(map.values());
  }, [questions]);

  const lastModuleKey = useRef(null);

  /**
   * Re-selects every module whenever the available module set changes - i.e.
   * when a financial year switch refetches questions and yields a different set
   * of modules. Without this, selectedModules keeps the previous year's ids and
   * filteredQuestions silently hides any module newly introduced in the new
   * year. Guarded by a sorted value signature so unrelated re-renders and
   * same-year refetches that return identical modules leave the user's current
   * selection untouched.
   */
  useEffect(() => {
    const moduleKey = derivedModuleOptions
      .map((m) => m.value)
      .sort()
      .join("|");
    if (moduleKey === lastModuleKey.current) return;
    lastModuleKey.current = moduleKey;
    setSelectedModules(derivedModuleOptions.map((m) => m.value));
  }, [derivedModuleOptions]);

  const groupedModules = useMemo(
    () => groupQuestionsByModule(filteredQuestions),
    [filteredQuestions]
  );

  // Search matches a question's title or any of its answer values.
  const filteredGroupedModules = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return groupedModules;

    return Object.entries(groupedModules).reduce((acc, [moduleName, moduleQuestions]) => {
      const matched = moduleQuestions.filter((question) => {
        if (question.title?.toLowerCase().includes(search)) return true;

        const answers = quickViewMap[question.questionId] || {};
        return Object.values(answers).some((cell) =>
          String(cell?.value ?? "").toLowerCase().includes(search)
        );
      });

      if (matched.length > 0) acc[moduleName] = matched;
      return acc;
    }, {});
  }, [groupedModules, quickViewMap, searchTerm]);

  const selectedParentLocations = useMemo(
    () => getSelectedParentLocations(selectedLocationIds, locations),
    [selectedLocationIds, locations]
  );

  // Number of sheets a "By Location" export would produce: one per selected
  // location, or one per selected sub-location for locations that have some.
  // The Combined/By-Location toggle only makes sense when this exceeds one.
  const perLocationSheetCount = useMemo(
    () =>
      selectedParentLocations.reduce((acc, parent) => {
        const selectedSubs = subLocations.filter(
          (sub) =>
            String(sub.parentValue) === String(parent.value) &&
            selectedSubLocationIds.map(String).includes(String(sub.value))
        );
        return acc + (selectedSubs.length > 0 ? selectedSubs.length : 1);
      }, 0),
    [selectedParentLocations, subLocations, selectedSubLocationIds]
  );
  const showExportModeToggle = perLocationSheetCount > 1;

  // Sub-locations belong to a parent, so only offer those whose parent is in
  // the current location selection. With every location selected (the
  // default), all sub-locations are available.
  const availableSubLocations = useMemo(() => {
    if (selectedLocationIds.length === 0 || selectedLocationIds.length === locations.length) {
      return subLocations;
    }
    const parentIds = new Set(selectedLocationIds.map((id) => String(id)));
    return subLocations.filter((sub) => parentIds.has(sub.parentValue));
  }, [subLocations, selectedLocationIds, locations]);

  /**
   * Updates the location selection and drops any staged sub-location whose
   * parent is no longer selected (pruning belongs in the handler, not an
   * effect).
   *
   * @param {string[]} nextLocationIds - New location selection.
   * @returns {void}
   */
  const handleLocationsChange = (nextLocationIds) => {
    setSelectedLocationIds(nextLocationIds);
    setSelectedSubLocationIds((prev) =>
      prev.filter((subId) => {
        const sub = subLocations.find((s) => String(s.value) === String(subId));
        return sub && nextLocationIds.map(String).includes(sub.parentValue);
      })
    );
  };

  /**
   * Builds the per-sheet render maps for the "By Location" export. Each
   * selected location becomes one sheet, unless it has selected sub-locations,
   * in which case it fans out into one sheet per selected sub-location (that
   * location's total is not exported separately). A location with no selected
   * sub-locations exports its full total.
   *
   * @returns {Array<{label: string, quickViewMap: Object}>} One entry per sheet.
   */
  const buildLocationSheets = () =>
    selectedParentLocations.flatMap((parent) => {
      const selectedSubsForParent = subLocations.filter(
        (sub) =>
          String(sub.parentValue) === String(parent.value) &&
          selectedSubLocationIds.map(String).includes(String(sub.value))
      );

      const sheetFor = (label, subIds) => ({
        label,
        quickViewMap: buildQuickViewMap({
          rows: rawRows,
          questions: filteredQuestions,
          restrictLocationIds: [parent.value],
          selectedSubLocationIds: subIds,
          subLocations,
        }),
      });

      if (selectedSubsForParent.length > 0) {
        return selectedSubsForParent.map((sub) =>
          sheetFor(`${parent.label} - ${sub.label}`, [sub.value])
        );
      }
      return [sheetFor(parent.label, [])];
    });

  /**
   * Exports the current view to Excel, either combined into one sheet or split
   * into a sheet per location / selected sub-location. Uses the same
   * "combined" | "byLocation" vocabulary as exportMode so no translation is
   * needed between the toggle's state and the export arrangement it selects.
   *
   * @param {"combined"|"byLocation"} sheetMode - Chosen sheet arrangement.
   * @returns {void}
   */
  const handleExport = (sheetMode) => {
    if (sheetMode === "byLocation") {
      downloadQuickViewExcelPerLocation({
        groupedModules: filteredGroupedModules,
        periods,
        locationMaps: buildLocationSheets(),
        financialYearValue,
        unitSettingsMap,
      });
    } else {
      downloadQuickViewExcel({
        groupedModules: filteredGroupedModules,
        periods,
        quickViewMap,
        financialYearValue,
        unitSettingsMap,
      });
    }
  };

  const shellProps = { sidebarExpanded, onSidebarToggle: setSidebarExpanded };

  if (!capabilitiesLoaded) {
    return (
      <QuickViewShell {...shellProps}>
        <Loader label="Loading..." />
      </QuickViewShell>
    );
  }

  if (!hasPermission("kpi.view")) {
    return <Redirect to="/home" />;
  }

  // Only the question list gates the page. Values arrive from a separate,
  // concurrent request, so the table renders as soon as the questions land and
  // fills in its cells when the answers follow.
  if (loadingQuestions) {
    return (
      <QuickViewShell {...shellProps}>
        <Loader label="Loading questions..." />
      </QuickViewShell>
    );
  }

  return (
    <QuickViewShell {...shellProps}>
      <QuickViewHeader
        onDownload={() => handleExport(showExportModeToggle ? exportMode : "combined")}
        downloadDisabled={Object.keys(filteredGroupedModules).length === 0}
        exportMode={exportMode}
        onExportModeChange={setExportMode}
        showExportModeToggle={showExportModeToggle}
      />

      {answersFailed && (
        <div className="qv-partial" role="alert">
          <span className="qv-partial__icon">
            <AlertTriangle size={18} aria-hidden="true" />
          </span>
          <div className="qv-partial__text">
            <strong>Values couldn’t be loaded</strong>
            <span>The questions below are shown without their answers.</span>
          </div>
          <button
            type="button"
            className="qv-partial__retry"
            onClick={retryAnswers}
            disabled={retrying}
          >
            <RefreshCw
              size={14}
              aria-hidden="true"
              className={retrying ? "qv-partial__spin" : ""}
            />
            {retrying ? "Retrying…" : "Retry"}
          </button>
        </div>
      )}

      <QuickViewFilters
        searchText={searchTerm}
        onSearchChange={setSearchTerm}
        financialYear={financialYear}
        financialYearId={financialYearId}
        onFinancialYearChange={setFinancialYearId}
        locations={locations}
        selectedLocationIds={selectedLocationIds}
        onLocationsChange={handleLocationsChange}
        subLocations={availableSubLocations}
        hasSubLocations={subLocations.length > 0}
        selectedSubLocationIds={selectedSubLocationIds}
        onSubLocationsChange={setSelectedSubLocationIds}
        moduleOptions={derivedModuleOptions}
        selectedModules={selectedModules}
        onModulesChange={setSelectedModules}
        periodAggregation={periodAggregation}
        onPeriodAggregationChange={setPeriodAggregation}
      />

      <QuickViewTable
        groupedModules={filteredGroupedModules}
        periods={periods}
        quickViewMap={quickViewMap}
        unitSettingsMap={unitSettingsMap}
        loadingValues={loadingAnswers}
      />
    </QuickViewShell>
  );
};

export default QuickView;
